import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import SEOHead from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { BookPlus, Send, ArrowLeft } from 'lucide-react';
import { suggestionsService } from '../services/suggestionsService';
import { useToast } from '../hooks/use-toast';

const SuggestBook = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    author: '',
    isbn: '',
    reason: '',
    user_email: '',
    user_name: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await suggestionsService.createBookSuggestion(formData);
      
      toast({
        title: "Book Suggested Successfully!",
        description: "Thank you for your suggestion. We'll review it and add it to our collection if appropriate.",
      });
      
      // Reset form
      setFormData({
        title: '',
        author: '',
        isbn: '',
        reason: '',
        user_email: '',
        user_name: ''
      });
      
    } catch (error) {
      console.error('Error submitting suggestion:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to submit book suggestion. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <SEOHead
        title="Suggest a Book - BookQuiz"
        description="Suggest a book you'd like to see turned into a quiz on BookQuiz."
        url="/suggest-book"
      />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="mb-6 min-h-[44px] touch-manipulation hover:bg-blue-50"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>

          <Card className="bg-white/80 backdrop-blur-sm shadow-2xl border-0">
            <CardHeader className="text-center pb-6">
              <div className="mx-auto w-16 h-16 bg-gradient-to-r from-green-600 to-blue-600 rounded-full flex items-center justify-center mb-4">
                <BookPlus className="h-8 w-8 text-white" />
              </div>
              <CardTitle className="text-2xl font-bold text-gray-900">
                Suggest a Book
              </CardTitle>
              <p className="text-gray-600">
                Help us expand our collection by suggesting books you'd like to see quizzes for
              </p>
            </CardHeader>
            
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="title">Book Title *</Label>
                    <Input
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder="e.g., The Great Gatsby"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="author">Author *</Label>
                    <Input
                      id="author"
                      name="author"
                      value={formData.author}
                      onChange={handleInputChange}
                      placeholder="e.g., F. Scott Fitzgerald"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="isbn">ISBN (Optional)</Label>
                  <Input
                    id="isbn"
                    name="isbn"
                    value={formData.isbn}
                    onChange={handleInputChange}
                    placeholder="e.g., 978-0-7432-7356-5"
                  />
                </div>
                
                <div>
                  <Label htmlFor="reason">Why should we add this book? *</Label>
                  <Textarea
                    id="reason"
                    name="reason"
                    value={formData.reason}
                    onChange={handleInputChange}
                    rows={4}
                    placeholder="Tell us why this book would be a great addition. Is it a classic? Popular in schools? Part of a series? The more details, the better!"
                    required
                  />
                </div>
                
                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Contact Information (Optional)</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="user_name">Your Name</Label>
                      <Input
                        id="user_name"
                        name="user_name"
                        value={formData.user_name}
                        onChange={handleInputChange}
                        placeholder="Your name"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="user_email">Your Email</Label>
                      <Input
                        id="user_email"
                        name="user_email"
                        type="email"
                        value={formData.user_email}
                        onChange={handleInputChange}
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    We'll only use this to thank you if we add your suggested book!
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 transition-all hover:scale-105"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  {loading ? 'Submitting...' : 'Submit Book Suggestion'}
                </Button>
              </form>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                <h4 className="font-semibold text-blue-900 mb-2">What happens next?</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Our team will review your suggestion</li>
                  <li>• We'll check if the book fits our collection</li>
                  <li>• If approved, we'll create quizzes using AI</li>
                  <li>• You'll be notified when it's available!</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default SuggestBook;